import { Alert, Stack } from '@mui/material'
import React, { useState } from 'react'

import { MonitorSearchField } from 'components/status-checks/form/status-check/common/MonitorSearchField'
import { MonitorSearchList } from 'components/status-checks/form/status-check/common/MonitorSearchList'
import {
  getRetryPredicateFn,
  GRAFANA_MONITOR_SEARCH_FIELD_WIDTH
} from 'components/status-checks/form/status-check/grafana/grafana-monitor-utils'
import { useGetGrafanaMonitors } from 'queries/grafana/use-get-grafana-monitors'
import { type GrafanaMonitor } from 'types/api/grafana/monitors'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'
import { INTEGRATION_NAME } from 'utils/status-checks/status-checks-utils'
import { messageFromResponseError } from 'utils/strings'

export const GrafanaMonitorSearch: React.FC<{
  disabled: boolean
  initialSearchValue?: string
  selectedValue: string | undefined
  onSelect(monitorId: string): void
}> = ({ disabled, initialSearchValue, selectedValue, onSelect }) => {
  const [queryValue, setQueryValue] = useState(initialSearchValue ?? '')

  const {
    data: grafanaMonitors,
    isError,
    isFetching,
    isIdle,
    isSuccess,
    error
  } = useGetGrafanaMonitors(
    {
      grafanaIntegrationName: INTEGRATION_NAME[ObservabilityToolType.GRAFANA],
      searchString: queryValue
    },
    { enabled: Boolean(queryValue), retry: getRetryPredicateFn }
  )

  // This is an error when BE has an issue communicating with Grafana.
  const errorMessage = isError
    ? decodeURIComponent(error.message) ??
      messageFromResponseError(error, 'Monitor search failed')
    : undefined

  return (
    <Stack spacing={2}>
      <MonitorSearchField
        disabled={disabled}
        helperText="This will filter the first 100 results based on Alert name or tags."
        isLoading={isFetching}
        sx={{ width: GRAFANA_MONITOR_SEARCH_FIELD_WIDTH }}
        onSubmit={setQueryValue}
      />
      {isError ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : (
        !isIdle && (
          <MonitorSearchList<GrafanaMonitor>
            isLoading={isFetching}
            rows={isSuccess ? grafanaMonitors.rules : []}
            selectedRowId={selectedValue}
            onClick={onSelect}
          />
        )
      )}
    </Stack>
  )
}
